import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { syncEnrolledCourses } from "../lib/offline";
import { ONBOARDING_PENDING_KEY } from "../components/HeaderOnboarding";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Si on revient du callback OAuth Google (/auth/callback?code=...), on laisse
    // AuthCallback gérer l'échange du code avant de checker /me.
    if (typeof window !== "undefined" && window.location.pathname === "/auth/callback") {
      setLoading(false);
      return;
    }
    // Only call /me if we have a token (silences 401 for anonymous visitors)
    const hasToken = typeof window !== "undefined" && localStorage.getItem("sf_token");
    if (!hasToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Synchronise les formations achetées pour un accès hors ligne, dès que
  // l'utilisateur est connecté, et à chaque retour de connexion. Silencieux.
  useEffect(() => {
    if (user) syncEnrolledCourses();
  }, [user]);
  useEffect(() => {
    const onOnline = () => syncEnrolledCourses();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const isTransientLoginError = (err) => {
    // Retry only server/network hiccups. Never retry wrong credentials.
    const status = err?.response?.status;
    return !status || status === 408 || status === 429 || status >= 500;
  };

  const loginJWT = async (email, password) => {
    const cleanEmail = String(email || "").trim().toLowerCase();
    let lastError = null;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const { data } = await api.post("/auth/login", { email: cleanEmail, password });
        localStorage.setItem("sf_token", data.access_token);
        setUser(data.user);
        return data.user;
      } catch (err) {
        lastError = err;
        if (!isTransientLoginError(err) || attempt === 2) {
          throw err;
        }
        await wait(500 * (attempt + 1));
      }
    }

    throw lastError;
  };

  const registerJWT = async (email, name, password, referralCode = null) => {
    const storedRef = typeof window !== "undefined" ? localStorage.getItem("sf_ref") : null;
    const referral_code = referralCode || storedRef || null;
    const { data } = await api.post("/auth/register", { email, name, password, referral_code });
    localStorage.setItem("sf_token", data.access_token);
    if (data?.user?.user_id) {
      localStorage.setItem(ONBOARDING_PENDING_KEY, data.user.user_id);
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sf_token");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { return null; }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, loginJWT, registerJWT, logout, refreshUser, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
